import React from 'react';

const GENDER_OPTIONS = [
  { value: 'male', label: 'זכר', icon: '👨' },
  { value: 'female', label: 'נקבה', icon: '👩' },
];

const PRIVACY_URL = (
  process.env.REACT_APP_PRIVACY_POLICY_URL || 'https://greatleadersplatform.onrender.com'
).trim();
const TERMS_URL = (
  process.env.REACT_APP_TERMS_OF_USE_URL || 'https://greatleadersplatform.onrender.com'
).trim();

const ONBOARDING_STEPS = ['name', 'gender', 'legal'];

function onboardingStepIndex(step) {
  const index = ONBOARDING_STEPS.indexOf(step);
  return index >= 0 ? index : 0;
}

function legalGreeting(name) {
  const trimmed = (name || '').trim();
  return trimmed ? `היי, ${trimmed}` : 'היי';
}

function legalLead(gender) {
  const approve = gender === 'female' ? 'אשרי' : 'אשר';
  return `לפני שממשיכים אנא ${approve} את המסמכים הבאים`;
}

function agreeLabel(gender) {
  return gender === 'female' ? 'מסכימה' : 'מסכים';
}

function PersonalAreaOnboarding({
  step,
  name,
  gender,
  acceptedPrivacyPolicy,
  acceptedTermsOfUse,
  isSubmitting,
  onNameChange,
  onGenderChange,
  onAcceptedPrivacyChange,
  onAcceptedTermsChange,
  onContinueName,
  onContinueGender,
  onComplete,
  onClose,
}) {
  const agree = agreeLabel(gender);

  const title =
    step === 'name'
      ? 'היי, ברוכים הבאים ל-Great Leaders'
      : step === 'gender'
        ? 'איך לפנות אליכם?'
        : null;

  const subtitle = step === 'name' ? 'איך קוראים לכם?' : null;
  const stepIndex = onboardingStepIndex(step);
  const bothLegalAccepted = acceptedPrivacyPolicy && acceptedTermsOfUse;

  return (
    <div className="contact-popup-overlay contact-popup-overlay--onboarding" onClick={onClose}>
      <div
        className={`contact-popup contact-popup--personal personal-area-onboarding personal-area-onboarding--${step}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="personal-onboarding-title"
      >
        <button type="button" className="close-popup close-popup--onboarding" onClick={onClose} aria-label="סגירה">
          ✕
        </button>

        <div className="personal-area-onboarding__steps" aria-hidden="true">
          {ONBOARDING_STEPS.map((onboardingStep, index) => (
            <span
              key={onboardingStep}
              className={[
                'personal-area-onboarding__step-dot',
                index < stepIndex ? 'personal-area-onboarding__step-dot--done' : '',
                index === stepIndex ? 'personal-area-onboarding__step-dot--current' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          ))}
        </div>

        {step === 'legal' ? (
          <header className="personal-area-onboarding__legal-head">
            <span className="personal-area-onboarding__legal-badge" aria-hidden="true">🛡️</span>
            <h2 id="personal-onboarding-title">
              <span className="personal-area-onboarding__legal-greeting">{legalGreeting(name)}</span>
              <span className="personal-area-onboarding__legal-lead">{legalLead(gender)}</span>
            </h2>
          </header>
        ) : (
          <>
            <h2 id="personal-onboarding-title">{title}</h2>
            {subtitle ? <p className="personal-area-onboarding__subtitle">{subtitle}</p> : null}
          </>
        )}

        {step === 'name' && (
          <form className="personal-area-form" onSubmit={onContinueName}>
            <div className="form-group form-group--name">
              <div className="input-shell">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="השם שלכם"
                  required
                  autoFocus
                />
              </div>
            </div>
            <button type="submit" className="verify-submit-btn personal-area-submit" disabled={!name.trim()}>
              המשך
            </button>
          </form>
        )}

        {step === 'gender' && (
          <form className="personal-area-form personal-area-form--gender" onSubmit={onContinueGender}>
            <div
              className="personal-area-gender-options"
              role="radiogroup"
              aria-labelledby="personal-onboarding-title"
            >
              {GENDER_OPTIONS.map((option, index) => (
                <label
                  key={option.value}
                  className={`personal-area-gender-option${gender === option.value ? ' is-selected' : ''}`}
                  style={{ '--gender-stagger': index }}
                >
                  <input
                    type="radio"
                    className="personal-area-gender-option__input"
                    name="personal-area-gender"
                    value={option.value}
                    checked={gender === option.value}
                    onChange={() => onGenderChange(option.value)}
                  />
                  <span className="personal-area-gender-option__icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <span className="personal-area-gender-option__label">{option.label}</span>
                </label>
              ))}
            </div>
            <button
              type="submit"
              className="verify-submit-btn personal-area-submit personal-area-submit--gender"
              disabled={!gender}
            >
              <span>המשך</span>
              <span className="personal-area-submit__arrow" aria-hidden="true">←</span>
            </button>
          </form>
        )}

        {step === 'legal' && (
          <form className="personal-area-form personal-area-form--legal" onSubmit={onComplete}>
            <div className="personal-area-legal-cards">
              <label
                className={`personal-area-legal-card${acceptedPrivacyPolicy ? ' is-checked' : ''}`}
                style={{ '--legal-stagger': 0 }}
              >
                <input
                  type="checkbox"
                  className="personal-area-legal-card__input"
                  checked={acceptedPrivacyPolicy}
                  onChange={(e) => onAcceptedPrivacyChange(e.target.checked)}
                />
                <span className="personal-area-legal-card__mark" aria-hidden="true" />
                <span className="personal-area-legal-card__content">
                  <span className="personal-area-legal-card__emoji" aria-hidden="true">🔒</span>
                  <span className="personal-area-legal-card__text">
                    קראתי ואני {agree} ל
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      מדיניות הפרטיות
                    </a>
                  </span>
                </span>
              </label>
              <label
                className={`personal-area-legal-card${acceptedTermsOfUse ? ' is-checked' : ''}`}
                style={{ '--legal-stagger': 1 }}
              >
                <input
                  type="checkbox"
                  className="personal-area-legal-card__input"
                  checked={acceptedTermsOfUse}
                  onChange={(e) => onAcceptedTermsChange(e.target.checked)}
                />
                <span className="personal-area-legal-card__mark" aria-hidden="true" />
                <span className="personal-area-legal-card__content">
                  <span className="personal-area-legal-card__emoji" aria-hidden="true">📜</span>
                  <span className="personal-area-legal-card__text">
                    קראתי ואני {agree} ל
                    <a
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      תנאי השימוש
                    </a>
                  </span>
                </span>
              </label>
            </div>
            <button
              type="submit"
              className={`verify-submit-btn personal-area-submit personal-area-submit--legal${
                bothLegalAccepted && !isSubmitting ? ' is-ready' : ''
              }`}
              disabled={
                isSubmitting || !acceptedPrivacyPolicy || !acceptedTermsOfUse
              }
            >
              <span>{isSubmitting ? 'שומר...' : 'כניסה לאיזור האישי'}</span>
              {!isSubmitting ? (
                <span className="personal-area-submit__arrow" aria-hidden="true">←</span>
              ) : null}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PersonalAreaOnboarding;
